import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { banco } from '../../db/banco'
import CartaoErro from '../../componentes/CartaoErro'
import { montarDadosPedido, type DadosPedidoPdf } from '../../pedidos/pdfDados'
import { lerAlvo, acharAlvoDoEnvio, type ResolucaoDoAlvo } from '../../pedidos/alvoDoEnvio'
import { nomeArquivoPdf } from '../../pedidos/nomeArquivoPdf'
import {
  compartilharArquivo, baixarArquivo, explicacaoSemCompartilhamento,
} from '../../pedidos/compartilhar'
import { PreviaSubPedido, PreviaConsolidado } from '../../pedidos/PreviaPedido'
import { mudarStatusSubPedido } from '../../pedidos/fechamento'
import { contatoDoRepresentante } from '../../lib/contatoDoRepresentante'

// Fase do envio DESTE documento. 'entrada' e antes de tentar; 'enviado' so
// depois de o compartilhamento dar certo (ou de ela dizer que enviou).
type Fase = 'entrada' | 'sem-compartilhamento' | 'enviado'

export default function PreviaEnvio() {
  const { id, alvo: alvoTexto } = useParams()
  const navegar = useNavigate()
  const { session } = useAuth()
  const [dados, setDados] = useState<DadosPedidoPdf | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [fase, setFase] = useState<Fase>('entrada')
  const [ocupado, setOcupado] = useState(false)

  const alvo = lerAlvo(alvoTexto)

  useEffect(() => {
    if (!id) return
    montarDadosPedido(id, contatoDoRepresentante(session?.user), session?.user.id)
      .then(setDados)
      .catch((excecao) =>
        setErro(excecao instanceof Error ? excecao.message : 'Erro ao montar o pedido.'),
      )
  }, [id, session])

  // Gera o PDF SO na hora de mandar: o renderer e o chunk mais pesado do app e
  // conferir na tela nao precisa dele.
  async function gerarBlob(resolucao: ResolucaoDoAlvo): Promise<{ blob: Blob; nome: string }> {
    const { pdf } = await import('@react-pdf/renderer')
    const { DocumentoSubPedido, DocumentoConsolidado } = await import('../../pedidos/PdfPedido')
    const nome = nomeArquivoPdf(dados!.cliente, dados!.pedido.numero, resolucao)
    if (resolucao.tipo === 'consolidado') {
      const blob = await pdf(<DocumentoConsolidado dados={dados!} />).toBlob()
      return { blob, nome }
    }
    const blob = await pdf(<DocumentoSubPedido dados={dados!} indice={resolucao.indice} />).toBlob()
    return { blob, nome }
  }

  // ⚠ Le o status FRESCO, nao o do snapshot: reenviar um sub-pedido ja 'enviado'
  // deve so confirmar, sem transicionar de novo (mudarStatusSubPedido lancaria
  // "Transição inválida" e isso viraria erro na tela).
  async function marcarComoEnviado(resolucao: ResolucaoDoAlvo) {
    if (resolucao.tipo !== 'marca') return
    // Uma marca pode ter VARIOS sub-pedidos (um por tabela). O documento e um
    // so, entao todos passam a 'enviado' juntos.
    for (const tabela of resolucao.marca.tabelas) {
      const atual = await banco.sub_pedidos.get(tabela.subPedido.id)
      if (atual && atual.status === 'fechado') await mudarStatusSubPedido(atual, 'enviado')
    }
  }

  async function compartilhar(resolucao: ResolucaoDoAlvo) {
    setOcupado(true)
    setErro(null)
    try {
      const { blob, nome } = await gerarBlob(resolucao)
      const resultado = await compartilharArquivo(blob, nome)
      if (resultado === 'compartilhado') {
        await marcarComoEnviado(resolucao)
        setFase('enviado')
      } else if (resultado === 'indisponivel') {
        setFase('sem-compartilhamento')
      }
      // 'cancelado': nada muda, de proposito. Ela desistiu no meio da folha de
      // compartilhar -- o pedido nao foi para lugar nenhum.
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao gerar o PDF.')
    } finally {
      setOcupado(false)
    }
  }

  async function baixar(resolucao: ResolucaoDoAlvo) {
    setOcupado(true)
    setErro(null)
    try {
      const { blob, nome } = await gerarBlob(resolucao)
      // ⚠ Baixar NAO marca enviado. Era o bug que este pacote existe para
      // consertar: no celular dela isso acontecia toda vez.
      baixarArquivo(blob, nome)
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao gerar o PDF.')
    } finally {
      setOcupado(false)
    }
  }

  async function jaEnviei(resolucao: ResolucaoDoAlvo) {
    setOcupado(true)
    try {
      await marcarComoEnviado(resolucao)
      setFase('enviado')
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao marcar como enviado.')
    } finally {
      setOcupado(false)
    }
  }

  if (erro) {
    return (
      <div className="p-4" data-testid="previa-erro">
        <CartaoErro titulo="Não foi possível preparar a prévia">{erro}</CartaoErro>
      </div>
    )
  }
  if (!alvo) {
    return (
      <div className="p-4 space-y-3" data-testid="previa-erro">
        <CartaoErro titulo="Documento não reconhecido">
          Volte e escolha novamente para quem enviar.
        </CartaoErro>
        <button
          onClick={() => navegar(`/pedidos/${id}/enviar`)}
          className="border border-teal-700 text-teal-700 rounded-lg px-3 py-2 text-sm"
        >
          Voltar para o envio
        </button>
      </div>
    )
  }
  if (!dados) return <p className="p-4 text-sm text-slate-500">Preparando a prévia…</p>

  const resolucao = acharAlvoDoEnvio(dados, alvo)
  if (!resolucao) {
    return (
      <div className="p-4 space-y-3" data-testid="previa-erro">
        <CartaoErro titulo="Esta marca não tem mais documento">
          Ela foi cancelada por inteiro, para a fábrica.
        </CartaoErro>
        <button
          onClick={() => navegar(`/pedidos/${id}/enviar`)}
          className="border border-teal-700 text-teal-700 rounded-lg px-3 py-2 text-sm"
        >
          Voltar para o envio
        </button>
      </div>
    )
  }

  // ⚠ NAO basta ser marca: um sub-pedido ja 'enviado' nao tem o que marcar, e
  // oferecer "Já enviei" nele so confunde. O snapshot serve aqui -- ele foi lido
  // ao abrir a tela, que e quando a pergunta faz sentido.
  const podeMarcar =
    resolucao.tipo === 'marca' &&
    resolucao.marca.tabelas.some((t) => t.subPedido.status === 'fechado')

  return (
    <section className="p-4 space-y-4">
      <p className="text-sm text-slate-500">
        Confira antes de enviar. Depois que sai, o documento já está com o cliente ou com a fábrica.
      </p>

      {resolucao.tipo === 'consolidado' ? (
        <PreviaConsolidado dados={dados} />
      ) : (
        <PreviaSubPedido dados={dados} indice={resolucao.indice} />
      )}

      {fase === 'entrada' && (
        <button
          data-testid="previa-compartilhar"
          onClick={() => void compartilhar(resolucao)}
          disabled={ocupado}
          className="w-full bg-teal-700 text-white rounded-lg py-3 font-medium disabled:opacity-50"
        >
          {ocupado ? 'Gerando…' : 'Compartilhar'}
        </button>
      )}

      {fase === 'sem-compartilhamento' && (
        <div
          data-testid="previa-sem-compartilhamento"
          className="bg-amber-100 border border-amber-300 text-amber-900 rounded-xl p-3 text-sm space-y-3"
        >
          <p>{explicacaoSemCompartilhamento(window.isSecureContext)}</p>
          <button
            data-testid="previa-baixar"
            onClick={() => void baixar(resolucao)}
            disabled={ocupado}
            className="w-full border border-amber-700 text-amber-900 rounded-lg py-2 disabled:opacity-50"
          >
            Baixar PDF
          </button>
          {podeMarcar && (
            <button
              data-testid="previa-ja-enviei"
              onClick={() => void jaEnviei(resolucao)}
              disabled={ocupado}
              className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50"
            >
              Já enviei este pedido
            </button>
          )}
        </div>
      )}

      {fase === 'enviado' && (
        <div
          data-testid="previa-enviado"
          className="bg-teal-50 border border-teal-300 text-teal-900 rounded-xl p-3 text-sm space-y-3"
        >
          <p>Arquivo enviado. Deseja baixar uma cópia?</p>
          <button
            data-testid="previa-baixar-copia"
            onClick={() => void baixar(resolucao)}
            disabled={ocupado}
            className="w-full border border-teal-700 text-teal-800 rounded-lg py-2 disabled:opacity-50"
          >
            Baixar cópia
          </button>
        </div>
      )}

      <button
        onClick={() => navegar(`/pedidos/${id}/enviar`)}
        className="w-full border border-slate-300 text-slate-700 rounded-lg py-2"
      >
        Voltar para o envio
      </button>
    </section>
  )
}
